import Container from "@/components/Container/Container";
import Section from "@/components/Section/Section";
import PageHero from "@/components/PageHero/PageHero";
import CTASection from "@/components/CTASection/CTASection";
import styles from "./BrainspottingPage.module.css";

const works = [
  {
    title: "Identifying a brainspot",
    body: "Locating an eye position connected to stored emotional material in the brain.",
  },
  {
    title: "Allowing natural processing",
    body: "The brain and body process the experience without forcing insight or interpretation.",
  },
  {
    title: "Supporting regulation",
    body: "Sessions are guided with attention to safety and nervous system regulation throughout.",
  },
];

const sessionSteps = [
  "Identifying a specific issue or emotional experience",
  "Locating the brainspot connected to that experience",
  "Allowing the brain and body to process the material while remaining supported in therapy",
];

export default function BrainspottingPage() {
  return (
    <>
      <PageHero
        eyebrow="Brainspotting therapy"
        title="Where you look affects how you feel."
        intro="Brainspotting is a powerful brain-body therapy designed to help process trauma, emotional pain, and deeply held experiences that may not be easily accessed through traditional talk therapy."
        accent="bloom"
      />

      <Section spacing="loose">
        <Container width="default" className={styles.intro}>
          <span className="eyebrow">What is Brainspotting?</span>
          <h2>A focused, brain-based pathway to healing.</h2>
          <p>
            The method is based on the principle that &ldquo;where you look affects how you
            feel.&rdquo; By identifying specific eye positions connected to emotional experiences
            stored in the brain, Brainspotting allows the brain and body to process unresolved
            experiences more effectively.
          </p>
        </Container>
      </Section>

      <Section tone="soft" spacing="loose">
        <Container width="default">
          <div className={styles.sectionHeader}>
            <span className="eyebrow">How it works</span>
            <h2>Allowing the brain&rsquo;s natural healing to unfold.</h2>
            <p>
              When a person experiences trauma or overwhelming stress, the brain may store those
              experiences in ways that remain unresolved. This can lead to emotional triggers,
              anxiety, or patterns that feel difficult to change. Brainspotting helps access these
              deeper brain processes by:
            </p>
          </div>
          <div className={styles.worksGrid}>
            {works.map((w, idx) => (
              <article key={w.title} className={styles.workCard}>
                <span className={styles.stepIndex}>0{idx + 1}</span>
                <h3>{w.title}</h3>
                <p>{w.body}</p>
              </article>
            ))}
          </div>
          <p className={styles.afterGrid}>
            Rather than forcing insight, Brainspotting allows the brain&rsquo;s natural healing
            mechanisms to unfold.
          </p>
        </Container>
      </Section>

      <Section spacing="loose">
        <Container width="default" className={styles.session}>
          <span className="eyebrow">What a session is like</span>
          <h2>Paced, supported, and led by you.</h2>
          <ol className={styles.steps}>
            {sessionSteps.map((step, idx) => (
              <li key={step}>
                <span className={styles.stepIndex}>0{idx + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <p>
            Clients remain in control of the pace of the process, and sessions are guided with
            attention to safety and nervous system regulation.
          </p>
        </Container>
      </Section>

      <Section tone="accent" spacing="loose">
        <Container width="default" className={styles.telehealth}>
          <span className="eyebrow">Brainspotting via telehealth</span>
          <h2>Effective from the comfort of home.</h2>
          <p>
            Brainspotting can be effectively conducted through secure telehealth sessions. With
            the right setup and guidance, clients can engage in the process from their own home
            while still receiving the full therapeutic benefits.
          </p>
        </Container>
      </Section>

      <Section spacing="loose">
        <Container width="default" className={styles.right}>
          <h2>Is Brainspotting right for you?</h2>
          <p>
            If you have tried traditional talk therapy but still feel stuck in certain emotional
            patterns, Brainspotting may offer a deeper pathway for healing. I integrate
            Brainspotting with somatic and relational therapy approaches to support lasting
            emotional change.
          </p>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
